import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Button,
  Container,
  Grid,
  Alert,
  ButtonGroup,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem
} from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth, Warehouse } from '../../../contexts/AuthContext';
import InventoryWarehouseModal from '../inventory/InventoryWarehouseModal';
import WarehouseItems from './WarehouseItems';
import WarehouseTransferModal from './WarehouseTransferModal';
import TransferTickets from './TransferTickets';
import WarehouseStockMovements from './WarehouseStockMovements';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../shared/StatsCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation } from 'react-i18next';
import { getBranches as getBranchesAdapter, type Branch as BranchOption } from '../../../services/migration/branchDataService';

// Tab panel bileşeni
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`warehouse-tabpanel-${index}`}
      aria-labelledby={`warehouse-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ width: '100%' }}>{children}</Box>}
    </div>
  );
}

// Depo veri yapısı
// Warehouse tipi AuthContext'ten import ediliyor

// Stok öğesi veri yapısı
// ⚠️ NOT: quantity ve warehouseId artık WarehouseStock tablosunda
interface InventoryItem {
  id: string;
  name: string;
  quantity: number; // Geçici - Excel için backend'den gelecek
  warehouseId: string | null; // Geçici - Excel için backend'den gelecek
  isActive: boolean;
  unit?: string; // Birim
  code?: string; // Ürün kodu
  barcode?: string; // Barkod
  warehouseName?: string; // Depo adı (Excel için)
  category?: string; // Kategori
  criticalStock?: number; // Kritik stok seviyesi
}

// Transfer fişi veri yapısı
interface TransferTicket {
  id: string;
  fromWarehouseId: string;
  fromWarehouseName?: string;
  toWarehouseId: string;
  toWarehouseName?: string;
  date: string;
  createdAt: string;
  status: string;
  notes?: string;
  items: TransferItem[];
}

// Transfer edilen ürün veri yapısı
interface TransferItem {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unit?: string;
}

interface WarehouseComponentProps {
  isManager?: boolean;
  managerBranchId?: string | null;
  /** warehouse_adjust yetkisi (yalnızca manager modunda anlamlı) */
  canAdjust?: boolean;
  /** warehouse_movements yetkisi (yalnızca manager modunda anlamlı) */
  canViewMovements?: boolean;
}

const WarehouseComponent: React.FC<WarehouseComponentProps> = ({ isManager = false, managerBranchId = null, canAdjust = false, canViewMovements = false }) => {
  const showMovementsTab = isManager && canViewMovements;
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { warehouses: allWarehouses, branches } = useAuth();

  // Depo.BranchesId SQL Id ya da Firebase Id taşıyabilir; ikisi de eşleşmeli.
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  useEffect(() => {
    if (!isManager) return;
    getBranchesAdapter('')
      .then(setBranchOptions)
      .catch((err) => console.warn('[Warehouse] Şubeler yüklenemedi:', err));
  }, [isManager]);

  const branchAliases = useMemo(() => {
    if (!managerBranchId) return null;
    const aliases = new Set<string>([managerBranchId]);
    const b = branchOptions.find(x => x.id === managerBranchId || String(x.sqlId) === managerBranchId);
    if (b) {
      aliases.add(b.id);
      if (b.sqlId != null) aliases.add(String(b.sqlId));
    }
    return aliases;
  }, [managerBranchId, branchOptions]);

  const warehouses = useMemo(
    () => (isManager
      ? allWarehouses.filter(w => !!w.branchesId && !!branchAliases?.has(String(w.branchesId)))
      : allWarehouses),
    [isManager, allWarehouses, branchAliases]
  );

  const getBranchName = (branchesId: string | null | undefined): string => {
    if (!branchesId) return '-';
    const branch = branches.find(b => b.id === branchesId);
    return branch ? branch.name : '-';
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [loadingExport, setLoadingExport] = useState(false);

  // Depo istatistik hesaplamaları
  const warehouseStats = useMemo(() => {
    const totalWarehouses = warehouses.length;
    const totalActiveItems = warehouses.reduce((sum, w) => sum + (w.activeItemCount || 0), 0);
    const defaultWarehouse = warehouses.find(w => w.isDefault);
    const totalStockCount = warehouses.reduce((sum, w) => sum + (w.stockCount || 0), 0);
    return { totalWarehouses, totalActiveItems, defaultWarehouse, totalStockCount };
  }, [warehouses]);

  // Depo dashboard özet verileri (backend'den)
  const [warehouseSummary, setWarehouseSummary] = useState<{
    totalStockQuantity: number; criticalStockCount: number;
    todayInCount: number; todayOutCount: number; idleProductCount: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadWarehouseSummary = async (compId: string, warehouseId?: string) => {
    try {
      setSummaryLoading(true);
      const response = await accountingApi.get(ACCOUNTING_API.WAREHOUSE_SUMMARY, {
        params: warehouseId ? { warehouseId } : undefined,
      });
      const result = response.data;
      const data = result.data || result.Data || result;
      setWarehouseSummary({
        totalStockQuantity: data.totalStockQuantity ?? data.TotalStockQuantity ?? 0,
        criticalStockCount: data.criticalStockCount ?? data.CriticalStockCount ?? 0,
        todayInCount: data.todayInCount ?? data.TodayInCount ?? 0,
        todayOutCount: data.todayOutCount ?? data.TodayOutCount ?? 0,
        idleProductCount: data.idleProductCount ?? data.IdleProductCount ?? 0,
      });
    } catch (err) {
      console.error('❌ [WAREHOUSE] Summary yükleme hatası:', err);
    } finally {
      setSummaryLoading(false);
    }
  };
  
  // ⚠️ NOT: inventoryItems, transferTickets, loadingItems, loadingTransfers 
  // state'leri kaldırıldı - Artık her sekme kendi component'inde veri yönetiyor
  
  // Tab değişimini yönet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // ⚠️ NOT: Her sekme kendi component'inde veri yükler (SQL ile)
    // loadInventoryItems ve loadTransferTickets çağrıları kaldırıldı
  };
  
  // Şirket ID'sini ve depoları yükleme
  useEffect(() => {
    const loadCompanyAndData = async () => {
      try {
        setLoading(true);
        
        // Kullanıcı oturum açmış mı kontrol et
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setLoading(false);
          setError(tt('Kullanıcı oturumu açık değil.', 'User session is not active.'));
          return;
        }
        
        // Kullanıcının şirket ID'sini al (Cloud Functions)
        const userData = await firebaseDataService.getUserData(uid);
        
        if (!userData) {
          setLoading(false);
          setError(tt('Kullanıcı bilgileri bulunamadı.', 'User information not found.'));
          return;
        }
        
        const companyId = userData.companyId;
        setCompanyId(companyId || null);
        
        if (!companyId) {
          setLoading(false);
          setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
          return;
        }
        
        // ✅ Depolar artık AuthContext'ten otomatik geliyor
        
        // Depo özet istatistiklerini yükle (manager: aşağıdaki efekt şube depolarıyla yükler)
        if (!isManager) loadWarehouseSummary(companyId);
        
        // ⚠️ NOT: Depo Ürünleri ve Transfer Fişleri sekmeleri 
        // artık kendi component'lerinde SQL ile veri yüklüyor
        // loadInventoryItems ve loadTransferTickets fonksiyonları kaldırıldı
        
        // Başarılı yükleme - loading'i kapat
        setLoading(false);
        
      } catch (error) {
        console.error('Şirket veya veri yüklenirken hata:', error);
        setLoading(false);
        setError(tt('Veriler yüklenirken bir hata oluştu.', 'An error occurred while loading data.'));
      }
    };
    
    loadCompanyAndData();
    
    // ⚠️ NOT: Firebase listener cleanup kaldırıldı - Artık SQL kullanılıyor
    // return () => { ... }
  }, []);

  // Manager: özet yalnızca şube depoları üzerinden. Tek depo varsa o depo istenir; birden çoksa
  // backend kapsamdaki depolarla sınırlar. Şubenin deposu yoksa özet boş gösterilir.
  const managerWarehouseKey = warehouses.map(w => w.id).join(',');
  useEffect(() => {
    if (!isManager || !companyId) return;
    if (warehouses.length === 0) {
      setWarehouseSummary({ totalStockQuantity: 0, criticalStockCount: 0, todayInCount: 0, todayOutCount: 0, idleProductCount: 0 });
      return;
    }
    loadWarehouseSummary(companyId, warehouses.length === 1 ? warehouses[0].id : undefined);
  }, [isManager, companyId, managerWarehouseKey]);

  // ⚠️ DEPRECATED: Bu fonksiyon artık kullanılmıyor - WarehouseItems component'i SQL kullanıyor
  // Depo ürünlerini yükleme (ESKI - Firebase)
  // const loadInventoryItems = (companyId: string) => {
  //   // Bu sekme artık WarehouseItems component'inde SQL ile yükleniyor
  // };
  
  // ⚠️ DEPRECATED: Bu fonksiyon artık kullanılmıyor - TransferTickets component'i SQL kullanıyor
  // Transfer fişlerini yükleme (ESKI - Firebase)
  // const loadTransferTickets = (companyId: string) => {
  //   // Bu sekme artık TransferTickets component'inde SQL ile yükleniyor
  // };

  // Modal işlemleri
  const handleOpenWarehouseModal = () => {
    setIsWarehouseModalOpen(true);
  };

  const handleCloseWarehouseModal = () => {
    setIsWarehouseModalOpen(false);
  };

  // Depo Stok Aktarım Modalı
  const handleOpenTransferModal = () => {
    setIsTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      
      // Sekmeye göre farklı içerik
      if (tabValue === 0) {
        // Depolar sekmesi
        await exportWarehousesToExcel(workbook);
      } else if (tabValue === 1) {
        // ⚠️ TODO: Depo ürünleri sekmesi - WarehouseItems component'inden veri alınmalı
        // Şimdilik Excel export çalışmayacak, component içinde implemente edilmeli
        setError(tt('Depo Ürünleri Excel export için WarehouseItems component\'ine taşınmalı.', 'Warehouse Items Excel export should be moved to WarehouseItems component.'));
        setLoadingExport(false);
        return;
      } else if (tabValue === 2) {
        // ⚠️ TODO: Transfer fişleri sekmesi - TransferTickets component'inden veri alınmalı
        // Şimdilik Excel export çalışmayacak, component içinde implemente edilmeli
        setError(tt('Transfer Fişleri Excel export için TransferTickets component\'ine taşınmalı.', 'Transfer Tickets Excel export should be moved to TransferTickets component.'));
        setLoadingExport(false);
        return;
      }
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const tabNames = [tt('Depolar', 'Warehouses'), tt('Depo_Urunleri', 'Warehouse_Items'), tt('Transfer_Fisleri', 'Transfer_Tickets')];
      const fileName = `${tabNames[tabValue]}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log(`Excel dışa aktarma tamamlandı.`);
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };
  
  // Depoları Excel'e aktarma
  const exportWarehousesToExcel = async (workbook: ExcelJS.Workbook) => {
    const worksheet = workbook.addWorksheet(tt('Depolar', 'Warehouses'));
    
    // Başlık satırı
    worksheet.mergeCells('A1:E1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = tt('Depo Listesi', 'Warehouse List');
    titleCell.style = {
      font: { bold: true, size: 14, color: { argb: 'FF000000' } },
      alignment: { horizontal: 'center' }
    };
    
    // Boş satır ekle
    worksheet.addRow([]);
    
    // Tablo başlıkları
    const headerRow = worksheet.addRow([
      tt('Depo Adı', 'Warehouse Name'),
      tt('Şube', 'Branch'),
      tt('Durum', 'Status'),
      tt('Adres', 'Address'),
      tt('Açıklama', 'Description'),
      tt('Ürün Sayısı', 'Item Count')
    ]);
    
    // Başlık stili
    headerRow.eachCell((cell) => {
      cell.style = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };
    });
    
    // Depo verilerini ekle
    warehouses.forEach((warehouse) => {
      worksheet.addRow([
        warehouse.name,
        getBranchName(warehouse.branchesId),
        warehouse.isDefault ? tt('Varsayılan', 'Default') : tt('Normal', 'Normal'),
        warehouse.address || '-',
        warehouse.description || '-',
        warehouse.activeItemCount || 0
      ]);
    });
    
    // Sütun genişliklerini ayarla
    worksheet.getColumn(1).width = 30; // Depo Adı
    worksheet.getColumn(2).width = 20; // Şube
    worksheet.getColumn(3).width = 15; // Durum
    worksheet.getColumn(4).width = 30; // Adres
    worksheet.getColumn(5).width = 40; // Açıklama
    worksheet.getColumn(6).width = 15; // Ürün Sayısı
    
    // Zebrastripe (alternatif satır renklendirme)
    for (let i = 4; i <= worksheet.rowCount; i++) {
      const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
      worksheet.getRow(i).eachCell(cell => {
        cell.style = {
          ...cell.style,
          fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
          border: {
            top: { style: 'thin' as const },
            left: { style: 'thin' as const },
            bottom: { style: 'thin' as const },
            right: { style: 'thin' as const }
          }
        };
      });
    }
  };
  
  // ⚠️ DEPRECATED: Bu fonksiyon artık kullanılmıyor - WarehouseItems component'i kendi Excel export'unu yapmalı
  // Depo ürünlerini Excel'e aktarma (ESKİ - Kullanılmıyor)
  // const exportInventoryItemsToExcel = async (workbook: ExcelJS.Workbook) => {
  //   // Bu fonksiyon WarehouseItems component'ine taşınmalı
  // };
  
  // ⚠️ DEPRECATED: Bu fonksiyon artık kullanılmıyor - TransferTickets component'i kendi Excel export'unu yapmalı
  // Transfer fişlerini Excel'e aktarma (ESKİ - Kullanılmıyor)
  /* 
  const exportTransferTicketsToExcel = async (workbook: ExcelJS.Workbook) => {
    // Bu fonksiyon TransferTickets component'ine taşınmalı
  };
  */

  // AccountingDataTable kolon tanımları
  const warehouseColumns: DataTableColumn<Warehouse>[] = [
    {
      id: 'rowNumber',
      label: tt('Sıra', 'No'),
      width: '60px',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{index + 1}</Box>
      ),
    },
    {
      id: 'name',
      label: tt('Depo Adı', 'Warehouse Name'),
      field: 'name',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarehouseIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          {row.name}
        </Box>
      ),
    },
    {
      id: 'branchesId',
      label: tt('Şube', 'Branch'),
      sortable: false,
      render: (row) => (
        <Typography variant="body2" color={row.branchesId ? 'text.primary' : 'text.secondary'}>
          {getBranchName(row.branchesId)}
        </Typography>
      ),
    },
    {
      id: 'isDefault',
      label: tt('Durum', 'Status'),
      sortable: true,
      render: (row) => (
        <Typography
          variant="caption"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: row.isDefault ? 'success.light' : 'text.disabled',
            color: row.isDefault ? 'success.contrastText' : 'white',
            display: 'inline-block',
            // Durum etiketleri yatayda eşit genişlikte ve ortalı görünür
            minWidth: '88px',
            textAlign: 'center',
          }}
        >
          {row.isDefault ? tt('Varsayılan', 'Default') : tt('Normal', 'Normal')}
        </Typography>
      ),
    },
    {
      id: 'address',
      label: tt('Adres', 'Address'),
      field: 'address',
      sortable: true,
      render: (row) => <>{row.address || '-'}</>,
    },
    {
      id: 'description',
      label: tt('Açıklama', 'Description'),
      field: 'description',
      sortable: true,
      render: (row) => <>{row.description || '-'}</>,
    },
    {
      id: 'activeItemCount',
      label: tt('Ürün Sayısı', 'Item Count'),
      field: 'activeItemCount',
      sortable: true,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 'bold' }}>{row.activeItemCount || 0}</Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ pb: 3 }}>
      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={(loading && warehouses.length === 0) || summaryLoading}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.blue}
            title={`${warehouseSummary?.totalStockQuantity ?? warehouseStats.totalStockCount}`}
            subtitle={tt('Toplam Stok Miktarı', 'Total Stock Quantity')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<WarningAmberIcon />}
            color={(warehouseSummary?.criticalStockCount ?? 0) > 0 ? STATS_COLORS.orange : STATS_COLORS.grey}
            title={`${warehouseSummary?.criticalStockCount ?? 0}`}
            subtitle={tt('Kritik Stok', 'Critical Stock')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<LocalShippingIcon />}
            color={STATS_COLORS.green}
            title={`${warehouseSummary?.todayInCount ?? 0} ${tt('Giriş', 'In')} / ${warehouseSummary?.todayOutCount ?? 0} ${tt('Çıkış', 'Out')}`}
            subtitle={tt('Bugünkü Stok Hareketi', 'Today Stock Movement')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<HourglassEmptyIcon />}
            color={(warehouseSummary?.idleProductCount ?? 0) > 0 ? STATS_COLORS.red : STATS_COLORS.grey}
            title={`${warehouseSummary?.idleProductCount ?? 0}`}
            subtitle={tt('Hareketsiz Ürün (90 gün)', 'Idle Product (90 days)')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Üst Başlık ve Butonlar - Responsive */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2,
        mb: 2
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
          {tt('Depo Yönetimi', 'Warehouse Management')}
        </Typography>
        
        {isMobile ? (
          // Mobil: Dikey Stack
          <Grid container spacing={1} sx={{ width: '100%' }}>
            {!isManager && (
              <>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    variant="outlined" 
                    startIcon={<WarehouseIcon />}
                    onClick={handleOpenWarehouseModal}
                    size="small"
                  >
                    {tt('Depolar', 'Warehouses')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    variant="outlined" 
                    startIcon={<SwapHorizIcon />}
                    onClick={handleOpenTransferModal}
                    size="small"
                  >
                    {tt('Transfer', 'Transfer')}
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Button 
                fullWidth
                variant="outlined" 
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}
                disabled={
                  loadingExport || 
                  (tabValue === 0 && (loading || warehouses.length === 0)) ||
                  (tabValue === 1) ||
                  (tabValue === 2)
                }
                size="small"
              >
                {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}
              </Button>
            </Grid>
          </Grid>
        ) : (
          // Desktop: Normal ButtonGroup
          <Grid container spacing={2} justifyContent="flex-end" sx={{ width: 'auto' }}>
            <Grid item>
              <ButtonGroup>
                {!isManager && (
                  <Button 
                    variant="outlined" 
                    startIcon={<WarehouseIcon />}
                    onClick={handleOpenWarehouseModal}
                  >
                    {tt('Depolar', 'Warehouses')}
                  </Button>
                )}
                {!isManager && (
                  <Button 
                    variant="outlined" 
                    startIcon={<SwapHorizIcon />}
                    onClick={handleOpenTransferModal}
                  >
                    {tt('Depo Stok Aktar', 'Transfer Warehouse Stock')}
                  </Button>
                )}
                <Button 
                  variant="outlined" 
                  startIcon={<FileDownloadIcon />}
                  onClick={exportToExcel}
                  disabled={
                    loadingExport || 
                    (tabValue === 0 && (loading || warehouses.length === 0)) ||
                    (tabValue === 1) ||
                    (tabValue === 2)
                  }
                >
                  {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Tablar - Responsive */}
      {isMobile ? (
        // Mobil: Dropdown
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{tt('Sekme Seç', 'Select Tab')}</InputLabel>
            <Select
              value={tabValue}
              label={tt('Sekme Seç', 'Select Tab')}
              onChange={(e) => handleTabChange(e as any, e.target.value as number)}
            >
              <SelectMenuItem value={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon fontSize="small" />
                  {tt('Depolar', 'Warehouses')}
                </Box>
              </SelectMenuItem>
              <SelectMenuItem value={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon fontSize="small" />
                  {tt('Depo Ürünleri', 'Warehouse Products')}
                </Box>
              </SelectMenuItem>
              {!isManager && (
                <SelectMenuItem value={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon fontSize="small" />
                    {tt('Transfer Fişleri', 'Transfer Tickets')}
                  </Box>
                </SelectMenuItem>
              )}
              {showMovementsTab && (
                <SelectMenuItem value={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapVertIcon fontSize="small" />
                    {tt('Stok Hareketleri', 'Stock Movements')}
                  </Box>
                </SelectMenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      ) : (
        // Desktop: Normal Tabs
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab 
              icon={<WarehouseIcon />} 
              label={tt('Depolar', 'Warehouses')} 
              id="warehouse-tab-0" 
              aria-controls="warehouse-tabpanel-0"
            />
            <Tab 
              icon={<InventoryIcon />} 
              label={tt('Depo Ürünleri', 'Warehouse Products')} 
              id="warehouse-tab-1" 
              aria-controls="warehouse-tabpanel-1"
            />
            {!isManager && (
              <Tab 
                icon={<ReceiptIcon />} 
                label={tt('Transfer Fişleri', 'Transfer Tickets')} 
                id="warehouse-tab-2" 
                aria-controls="warehouse-tabpanel-2"
              />
            )}
            {showMovementsTab && (
              <Tab 
                icon={<SwapVertIcon />} 
                label={tt('Stok Hareketleri', 'Stock Movements')} 
                id="warehouse-tab-2" 
                aria-controls="warehouse-tabpanel-2"
              />
            )}
          </Tabs>
        </Box>
      )}

      <Box sx={{ py: { xs: 1, md: 2 } }}>
        {/* Tab İçerikleri */}
        <TabPanel value={tabValue} index={0}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
    <Box sx={{ 
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
              height: '400px' 
            }}>
              <CircularProgress />
            </Box>
          ) : warehouses.length === 0 ? (
            <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
              borderRadius: 2
            }}>
        <WarehouseIcon 
          sx={{ 
            fontSize: 80, 
            color: 'primary.main',
                  mb: 2
          }} 
        />
        
              <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
                {tt('Depo Listesi', 'Warehouse List')}
        </Typography>
        
              <Typography variant="body1" color="text.secondary">
                {isManager
                  ? tt('Bu şubeye bağlı depo yok.', 'There is no warehouse linked to this branch.')
                  : tt('Henüz depo kaydı bulunmamaktadır. Yeni depo eklemek için sağ üstteki "Depolar" butonunu kullanabilirsiniz.', 'No warehouse record found yet. Use the "Warehouses" button at the top right to add a new warehouse.')}
        </Typography>
        
              {!isManager && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenWarehouseModal}
                  sx={{ mt: 3 }}
                >
                  {tt('Depo Ekle', 'Add Warehouse')}
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Desktop: Table View */}
              {!isMobile && (
                <AccountingDataTable<Warehouse>
                  columns={warehouseColumns}
                  rows={warehouses}
                  getRowId={(row) => row.id}
                  loading={false}
                  pagination={false}
                  emptyMessage={tt('Henüz depo eklenmemiş', 'No warehouse added yet')}
                  ariaLabel={tt('Depolar tablosu', 'warehouses table')}
                  columnResizeStorageKey="warehouse"
                />
              )}
              
              {/* Mobile: Card View */}
              {isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {warehouses.map((warehouse) => (
                    <Card 
                      key={warehouse.id}
                      sx={{ 
                        border: '1px solid',
                        borderColor: warehouse.isDefault ? 'success.light' : 'divider',
                        bgcolor: warehouse.isDefault ? 'rgba(76, 175, 80, 0.04)' : 'background.paper'
                      }}
                    >
                      <CardContent>
                        {/* Başlık - Depo Adı ve Durum */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <WarehouseIcon color="primary" sx={{ fontSize: 20 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {warehouse.name}
                            </Typography>
                          </Box>
                          <Chip 
                            label={warehouse.isDefault ? tt('Varsayılan', 'Default') : tt('Normal', 'Normal')}
                            size="small"
                            color={warehouse.isDefault ? 'success' : 'default'}
                          />
                        </Box>
                        
                        {/* Adres */}
                        {warehouse.address && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {tt('Adres:', 'Address:')}
                            </Typography>
                            <Typography variant="body2">
                              {warehouse.address}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Açıklama */}
                        {warehouse.description && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {tt('Açıklama:', 'Description:')}
                            </Typography>
                            <Typography variant="body2">
                              {warehouse.description}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Ürün Sayısı */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2, 
                          pt: 1.5, 
                          borderTop: '1px solid', 
                          borderColor: 'divider' 
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Ürün Sayısı:', 'Item Count:')}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {warehouse.activeItemCount || 0}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isManager && warehouses.length === 0 ? (
            <Alert severity="info">{tt('Bu şubeye bağlı depo yok.', 'There is no warehouse linked to this branch.')}</Alert>
          ) : (
            <WarehouseItems
              isManager={isManager}
              warehouses={isManager ? warehouses : undefined}
              canAdjust={canAdjust}
            />
          )}
        </TabPanel>

        {!isManager && (
          <TabPanel value={tabValue} index={2}>
            <TransferTickets companyId={companyId} />
          </TabPanel>
        )}

        {showMovementsTab && (
          <TabPanel value={tabValue} index={2}>
            {warehouses.length === 0 ? (
              <Alert severity="info">{tt('Bu şubeye bağlı depo yok.', 'There is no warehouse linked to this branch.')}</Alert>
            ) : (
              <WarehouseStockMovements warehouses={warehouses} />
            )}
          </TabPanel>
        )}
      </Box>

      {/* Modals (manager: depo tanımlama ve transfer kapalı) */}
      {!isManager && (
        <>
          <InventoryWarehouseModal
            open={isWarehouseModalOpen}
            onClose={handleCloseWarehouseModal}
          />
          
          {/* Depolar Arası Transfer Modalı */}
          <WarehouseTransferModal
            open={isTransferModalOpen}
            onClose={handleCloseTransferModal}
            warehouses={warehouses}
            companyId={companyId}
          />
        </>
      )}
    </Container>
  );
};

export default WarehouseComponent; 